import { useState } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Historial, type MovimientoHistorial } from "@/lib/api";
import { juntarPaginas } from "@/lib/historial";

/** Historial de movimientos (sin JSX). Admin y contador leen; sólo el admin deshace. */
export function useHistorialTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/historial`;
  const primera = useApiResource<Historial>(base);
  const esAdmin = membresia.rol === "admin";

  // Páginas de "Ver más" (las anteriores a la primera); se descartan al recargar.
  const [mas, setMas] = useState<MovimientoHistorial[]>([]);
  const [siguienteMas, setSiguienteMas] = useState<number | null | undefined>(undefined);
  const accionMas = useAsyncAction((antesDe: number) => api.get<Historial>(`${base}?antes_de=${antesDe}`));
  const siguiente = siguienteMas === undefined ? (primera.data?.siguiente ?? null) : siguienteMas;
  const items = juntarPaginas(primera.data?.items ?? [], mas);

  const verMas = async () => {
    if (siguiente === null) return;
    const pagina = await accionMas.run(siguiente);
    if (!pagina) return;
    setMas((m) => juntarPaginas(m, pagina.items));
    setSiguienteMas(pagina.siguiente);
  };

  const [aDeshacer, setADeshacer] = useState<MovimientoHistorial | null>(null);
  const accionDeshacer = useAsyncAction((id: number) => api.post<void>(`${base}/${id}/deshacer`).then(() => true));
  const pedirDeshacer = (m: MovimientoHistorial) => {
    accionDeshacer.limpiarError();
    setADeshacer(m);
  };
  const confirmarDeshacer = async () => {
    if (!aDeshacer) return;
    if (await accionDeshacer.run(aDeshacer.id)) {
      setADeshacer(null);
      setMas([]);
      setSiguienteMas(undefined);
      primera.recargar();
    }
  };

  return {
    esAdmin,
    cargando: primera.loading && !primera.data,
    errorCarga: primera.data ? null : (primera.error?.message ?? null),
    reintentar: primera.recargar,
    items,
    hayMas: siguiente !== null,
    verMas: () => void verMas(),
    cargandoMas: accionMas.loading,
    errorMas: accionMas.error,
    aDeshacer,
    pedirDeshacer,
    cancelarDeshacer: () => setADeshacer(null),
    confirmarDeshacer: () => void confirmarDeshacer(),
    deshaciendo: accionDeshacer.loading,
    errorDeshacer: accionDeshacer.error,
  };
}
